export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export interface PageInfo {
  cursor: string | null;
  hasMore: boolean;
  limit: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  pageInfo?: PageInfo | null;
}

// Token getter function type
type TokenGetter = () => Promise<string | null> | string | null;

class BaseApi {
  private baseURL: string;
  private static tokenGetter: TokenGetter | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Set a function to retrieve the authentication token
   * This should be called from the AuthContext or similar to provide token access
   */
  static setTokenGetter(getter: TokenGetter | null): void {
    BaseApi.tokenGetter = getter;
  }

  /**
   * Get the authentication token using the configured token getter
   */
  private async getToken(): Promise<string | null> {
    if (!BaseApi.tokenGetter) {
      return null;
    }
    try {
      const token = await BaseApi.tokenGetter();
      return token;
    } catch (error) {
      console.error('Failed to get authentication token:', error);
      return null;
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    const isFormData = options.body instanceof FormData;
    const { params, ...fetchOptions } = options;

    // Build query string from params using URLSearchParams
    let url = `${this.baseURL}${endpoint}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Build headers properly
    const headers: Record<string, string> = {};

    // Copy existing headers if they're a Record
    if (fetchOptions.headers) {
      if (fetchOptions.headers instanceof Headers) {
        fetchOptions.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(fetchOptions.headers)) {
        fetchOptions.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, fetchOptions.headers);
      }
    }

    // Only set Content-Type for JSON requests, not FormData
    // FormData needs to let the browser set the boundary automatically
    if (isFormData) {
      // Remove Content-Type if it exists - browser will set it with proper boundary
      delete headers['Content-Type'];
    } else if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    // Add authentication token if available
    const token = await this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      let errorData: any = null;

      // Try to parse error response body
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          // Extract error message from common error response formats
          if (errorData?.message) {
            errorMessage = errorData.message;
          } else if (errorData?.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        }
      } catch (e) {
        // If parsing fails, use statusText
      }

      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).response = errorData;
      throw error;
    }

    // Handle empty responses (e.g., 204 No Content for DELETE requests)
    if (response.status === 204 || response.statusText === 'No Content') {
      return {
        success: true,
        data: {} as T,
        message: 'Operation completed successfully',
      };
    }

    return response.json();
  }

  async get<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, options);
  }

  async post<T>(
    endpoint: string,
    body: any,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    const isFormData = body instanceof FormData;
    const requestBody = isFormData ? body : JSON.stringify(body);

    return this.request<T>(endpoint, { ...options, method: 'POST', body: requestBody });
  }

  async put<T>(
    endpoint: string,
    body: any,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    const isFormData = body instanceof FormData;
    const requestBody = isFormData ? body : JSON.stringify(body);

    return this.request<T>(endpoint, { ...options, method: 'PUT', body: requestBody });
  }

  async patch<T>(
    endpoint: string,
    body: any,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    const isFormData = body instanceof FormData;
    const requestBody = isFormData ? body : JSON.stringify(body);

    return this.request<T>(endpoint, { ...options, method: 'PATCH', body: requestBody });
  }

  async delete<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Stream Server-Sent Events (SSE) from an endpoint
   * @param endpoint - The API endpoint
   * @param options - Request options including body and params
   * @returns Async generator that yields parsed event data
   */
  async *stream<T = any>(
    endpoint: string,
    options: Omit<RequestInit, 'body'> & { params?: Record<string, any>; body?: any } = {},
  ): AsyncGenerator<T, void, unknown> {
    const { params, body, ...fetchOptions } = options;

    // Build URL with query params
    let url = `${this.baseURL}${endpoint}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };

    // Copy existing headers
    if (fetchOptions.headers) {
      if (fetchOptions.headers instanceof Headers) {
        fetchOptions.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(fetchOptions.headers)) {
        fetchOptions.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, fetchOptions.headers);
      }
    }

    // Add authentication token if available
    const token = await this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Prepare request body
    const requestBody = body ? JSON.stringify(body) : undefined;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        method: 'POST',
        headers,
        body: requestBody,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`SSE request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages (SSE format: "data: <json>\n\n")
          let eventEndIndex = buffer.indexOf('\n\n');

          while (eventEndIndex !== -1) {
            const eventData = buffer.substring(0, eventEndIndex);
            buffer = buffer.substring(eventEndIndex + 2); // Remove processed event and double newline

            // Parse SSE event
            const lines = eventData.split('\n');
            let eventDataContent = '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                eventDataContent = line.slice(6); // Remove 'data: ' prefix
                break;
              }
            }

            if (eventDataContent) {
              if (eventDataContent.trim() === '[DONE]') {
                return;
              }
              try {
                const parsed = JSON.parse(eventDataContent);
                yield parsed as T;
              } catch (e) {
                console.warn('Failed to parse SSE data:', eventDataContent, e);
                // If parsing fails, yield the raw data
                yield eventDataContent as T;
              }
            }

            // Check for next event
            eventEndIndex = buffer.indexOf('\n\n');
          }
        }

        // Process any remaining buffer
        if (buffer.trim()) {
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data.trim() !== '[DONE]') {
                try {
                  const parsed = JSON.parse(data);
                  yield parsed as T;
                } catch (e) {
                  console.warn('Failed to parse remaining SSE data:', data, e);
                  yield data as T;
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('SSE stream error:', error);
      throw error;
    }
  }
}

export default BaseApi;
